"use client";

import { useState, useCallback, type ReactNode } from "react";
import { ProductCard, type ProductCardProduct } from "@/components/product/ProductCard";
import { ProductListCard } from "@/components/product/ProductListCard";
import { ProductGrid } from "@/components/product/ProductGrid";
import { ProductListView } from "@/components/product/ProductListView";
import { ShopToolbar } from "@/components/product/ShopToolbar";
import { NoResultsState } from "@/components/product/NoResultsState";
import type { ViewMode } from "@/components/product/ViewToggle";

export interface ProductListingProps {
  products: ProductCardProduct[];
  total: number;
  /** Href for the "browse all" link when no results, e.g. "/wigs" */
  browseAllHref: string;
  /** Label for the browse all category name */
  browseAllLabel: string;
  /** Slot for mobile filter button */
  mobileFilters?: ReactNode;
  /** Whether to show the viewer count social proof badge */
  showViewerCount?: boolean;
}

export function ProductListing({
  products,
  total,
  browseAllHref,
  browseAllLabel,
  mobileFilters,
  showViewerCount = false,
}: ProductListingProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("grid");

  const handleViewChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, []);

  return (
    <>
      <ShopToolbar
        total={total}
        onViewChange={handleViewChange}
        mobileFilters={mobileFilters}
        showViewerCount={showViewerCount}
      />

      {products.length === 0 ? (
        <NoResultsState
          browseAllHref={browseAllHref}
          browseAllLabel={browseAllLabel}
        />
      ) : viewMode === "list" ? (
        <ProductListView>
          {products.map((product) => (
            <ProductListCard key={product.id} product={product} />
          ))}
        </ProductListView>
      ) : (
        <ProductGrid>
          {products.map((product, index) => (
            <ProductCard key={product.id} product={product} priority={index < 4} />
          ))}
        </ProductGrid>
      )}
    </>
  );
}
